import { useEffect, useState } from "react";
import { Button, Col, Container, Row, Spinner } from "react-bootstrap";
import * as icons from "react-bootstrap-icons";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { UnsubscribeConfirm, UnsubscribeGetDetails } from "../../redux/auth-reducer/auth-service";
import { readApiMessage } from "../../utils/helper/helper";
import {
  STOP_ALL_LABEL,
  UNSUBSCRIBE_STATE,
  canStopAll,
  canStopManager,
  stopManagerLabel,
  turnBackOnHint,
  unsubscribeState,
  unsubscribeText,
} from "../../utils/helper/unsubscribe";

/**
 * The page the link in every Broadcasting Center email opens: /Unsubscribe?token=...
 *
 * No sign-in: the link's token names the player and the Manager whose email held it, and the calls
 * carry no session, so a different account signed in on this browser does not matter. Opening the
 * page changes nothing - mail security scanners open every link in an email - until the player
 * presses "Stop emails from <Manager>" or "Stop all Manager emails". It cannot turn emails back on;
 * each state says where, in the signed-in settings, that is done. The mail app's own one-click
 * Unsubscribe never comes here: the server takes it directly.
 */
export const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [state, setState] = useState(UNSUBSCRIBE_STATE.LOADING);
  const [managerName, setManagerName] = useState("");
  const [stoppedAll, setStoppedAll] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let current = true;
    if (!token) {
      setState(UNSUBSCRIBE_STATE.INVALID);
      return undefined;
    }
    UnsubscribeGetDetails(token).then((res) => {
      if (!current) return;
      setManagerName(res?.data?.managerName || "");
      setState(unsubscribeState(res?.data));
    });
    return () => {
      current = false;
    };
  }, [token]);

  const stop = async (all) => {
    setBusy(true);
    const res = await UnsubscribeConfirm(token, all);
    setBusy(false);

    if (res?.data?.status === true) {
      setStoppedAll(all);
      setState(UNSUBSCRIBE_STATE.DONE);
      return;
    }
    if (res) toast.error(readApiMessage(res));
  };

  const { title, text } = unsubscribeText(state, managerName, stoppedAll);
  const hint = turnBackOnHint(state, stoppedAll);
  const finished = state === UNSUBSCRIBE_STATE.DONE || state === UNSUBSCRIBE_STATE.ALL_OFF;

  return (
    <section className="payment-success unsubscribe-page">
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6} className="text-center">
            {state === UNSUBSCRIBE_STATE.LOADING ? (
              <Spinner animation="border" variant="primary" />
            ) : (
              <>
                {state === UNSUBSCRIBE_STATE.INVALID ? (
                  <icons.ExclamationTriangle className="payment-check-cancel" />
                ) : finished ? (
                  <icons.Check2Circle className="payment-check" />
                ) : (
                  <icons.EnvelopeSlash className="unsubscribe-icon" />
                )}
                <h2 className="mt-3">{title}</h2>
                <p>{text}</p>

                {(canStopManager(state) || canStopAll(state)) && (
                  <div className="d-flex gap-2 justify-content-center flex-wrap mt-3">
                    {canStopManager(state) && (
                      <Button onClick={() => stop(false)} disabled={busy}>
                        {stopManagerLabel(managerName)}
                      </Button>
                    )}
                    {canStopAll(state) && (
                      <Button
                        variant={canStopManager(state) ? "outline-secondary" : "primary"}
                        onClick={() => stop(true)}
                        disabled={busy}
                      >
                        {STOP_ALL_LABEL}
                      </Button>
                    )}
                  </div>
                )}

                {hint && <p className="text-muted small mt-4">{hint}</p>}
                <Link className="btn btn-link" to="/Login">
                  Sign in
                </Link>
              </>
            )}
          </Col>
        </Row>
      </Container>
    </section>
  );
};
